import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";
import { Lock, User } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SetupAccountPage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [userName, setUserName] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchInfo = async () => {
      try {
        const res = await axios.get(`${API}/auth/setup-info/${token}`);
        setUserName(res.data.name);
      } catch {
        setError("This setup link is invalid or has already been used.");
      } finally {
        setLoading(false);
      }
    };
    fetchInfo();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    if (password !== confirmPassword) {
      toast.error("Passwords don't match");
      return;
    }

    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/auth/setup-password`, {
        token,
        password
      });
      if (res.data.session_token) {
        localStorage.setItem("session_token", res.data.session_token);
      }
      toast.success("Account set up successfully!");
      setTimeout(() => navigate("/my-dashboard"), 1000);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to set up account");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[hsl(var(--background))] flex items-center justify-center">
        <p className="text-[hsl(var(--muted-foreground))]">Loading...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[hsl(var(--background))] flex items-center justify-center p-4">
        <Card className="bg-[hsl(var(--card))] border-[hsl(var(--border))] max-w-md w-full">
          <CardContent className="p-8 text-center">
            <Lock size={48} className="mx-auto text-red-500 mb-4" />
            <h2 className="text-xl font-bold mb-2">Link Expired</h2>
            <p className="text-[hsl(var(--muted-foreground))]">{error}</p>
          </CardContent>
        </Card>
        <Toaster position="top-center" richColors />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[hsl(var(--background))] flex items-center justify-center p-4" data-testid="setup-account-page">
      <Card className="bg-[hsl(var(--card))] border-[hsl(var(--border))] max-w-md w-full">
        <CardHeader className="text-center">
          <div className="w-16 h-16 rounded-full bg-[hsl(var(--primary))]/20 flex items-center justify-center mx-auto mb-4">
            <User size={32} className="text-[hsl(var(--primary))]" />
          </div>
          <CardTitle className="text-2xl">Welcome, {userName}!</CardTitle>
          <p className="text-sm text-[hsl(var(--muted-foreground))] mt-2">
            Set a password to access your account
          </p>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4" data-testid="setup-password-form">
            <div>
              <label className="text-sm text-[hsl(var(--muted-foreground))] mb-1 block">Password</label>
              <Input
                type="password"
                placeholder="Choose a password (min 6 characters)"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="bg-[hsl(var(--secondary))] border-[hsl(var(--border))]"
                data-testid="setup-password-input"
                required
              />
            </div>
            <div>
              <label className="text-sm text-[hsl(var(--muted-foreground))] mb-1 block">Confirm Password</label>
              <Input
                type="password"
                placeholder="Confirm your password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="bg-[hsl(var(--secondary))] border-[hsl(var(--border))]"
                data-testid="setup-confirm-password-input"
                required
              />
            </div>
            <Button
              type="submit"
              disabled={submitting}
              className="w-full bg-[hsl(var(--primary))] text-black hover:bg-[hsl(var(--primary))]/90"
              data-testid="setup-submit-btn"
            >
              {submitting ? "Setting up..." : "Set Password & Continue"}
            </Button>
          </form>
        </CardContent>
      </Card>
      <Toaster position="top-center" richColors />
    </div>
  );
}
